/**
 * INQUIRY MODAL
 * 
 * Modal for composing an initial message to a seller about items
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendInquiry, getUserInfo } from '../../messaging/messagingService';
import type { AttachedItem } from '../../messaging/types';
import ItemChip from '../../messaging/components/ItemChip';
import { X, Send, Loader2, MessageSquare } from 'lucide-react';
import { showSuccess, showError } from '../../utils/toast';

interface InquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserId: string;
  sellerId: string;
  sellerUsername: string | null;
  items: AttachedItem[];
}

const InquiryModal: React.FC<InquiryModalProps> = ({
  isOpen,
  onClose,
  currentUserId,
  sellerId,
  sellerUsername,
  items
}) => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!message.trim() || sending) return;

    setSending(true);

    try {
      // Get user info for both participants
      const currentUserInfo = await getUserInfo(currentUserId);
      const sellerInfo = await getUserInfo(sellerId);

      // Send inquiry
      const conversationId = await sendInquiry(
        currentUserId,
        sellerId,
        currentUserInfo,
        sellerInfo,
        items,
        message.trim()
      );

      // Show success toast and navigate to the conversation
      showSuccess('Message sent!');
      onClose();
      navigate(`/messages/${conversationId}`);
    } catch (err) {
      console.error('Error sending inquiry:', err);
      showError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="glass-modal max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-600">
          <div className="flex items-center gap-2">
            <MessageSquare className="icon-md text-glass" />
            <h2 className="font-bold text-lg text-glass font-body">
              Message Seller
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-700/60 rounded-full transition-default"
          >
            <X className="icon-md text-glass-muted" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Recipient */}
          <div className="mb-4">
            <p className="text-sm text-glass-secondary mb-1 font-body">To:</p>
            <p className="font-bold text-glass font-body">
              {sellerUsername ? `@${sellerUsername}` : 'Seller'}
            </p>
          </div>

          {/* Items */}
          <div className="mb-4">
            <p className="text-sm text-glass-secondary mb-2 font-body">
              Regarding {items.length} item{items.length !== 1 ? 's' : ''}:
            </p>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {items.map((item, index) => (
                <ItemChip key={`${item.itemId}-${index}`} item={item} size="small" />
              ))}
            </div>
          </div>

          {/* Message input */}
          <div className="mb-4">
            <p className="text-sm text-glass-secondary mb-2 font-body">Message:</p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Hi! Is this still available?"
              rows={4}
              className="textarea-glass font-body"
              disabled={sending}
              autoFocus
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-slate-600 bg-glass-light">
          <button
            onClick={onClose}
            className="px-4 py-2 text-glass-secondary hover:bg-slate-700/60 rounded-lg transition-default font-body"
            disabled={sending}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!message.trim() || sending}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-default font-body ${
              message.trim() && !sending
                ? 'btn-primary'
                : 'bg-slate-600 text-glass-muted cursor-not-allowed'
            }`}
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="icon-sm" />
                Send Inquiry
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InquiryModal;
